import { useState } from 'react';
import { Loader2, Pencil, Plus } from 'lucide-react';

import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { useCreateDepartment, useDepartments, useUpdateDepartment } from '@/hooks/use-projects';
import { handleApiError } from '@/lib/api-client';
import type { Department } from '@/lib/types';

interface DepartmentDialogProps {
  department?: Department;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DepartmentDialog({ department, open, onOpenChange }: DepartmentDialogProps) {
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const [name, setName] = useState(department?.name ?? '');
  const [code, setCode] = useState(department?.code ?? '');
  const [error, setError] = useState('');
  const isPending = createDepartment.isPending || updateDepartment.isPending;

  const close = () => {
    setName('');
    setCode('');
    setError('');
    onOpenChange(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    try {
      if (department) {
        await updateDepartment.mutateAsync({
          id: department.id,
          data: { name: name.trim(), code: code.trim() },
        });
      } else {
        await createDepartment.mutateAsync({
          name: name.trim(),
          code: code.trim() || undefined,
        });
      }
      close();
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen ? onOpenChange(true) : close()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{department ? 'Edit department' : 'New department'}</DialogTitle>
            <DialogDescription>
              Departments group projects for ownership and reporting.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div data-field-container className="space-y-2">
              <Label htmlFor="settings-department-name">Name *</Label>
              <Input
                id="settings-department-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="settings-department-code">Code</Label>
              <Input
                id="settings-department-code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Optional short code"
              />
            </div>
            {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {department ? 'Save department' : 'Create department'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DepartmentsSettingsCard() {
  const { data, isLoading } = useDepartments({ page_size: 100 });
  const updateDepartment = useUpdateDepartment();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Department | undefined>();
  const [error, setError] = useState('');
  const departments = data?.results ?? [];

  const toggleActive = async (department: Department) => {
    setError('');
    try {
      await updateDepartment.mutateAsync({
        id: department.id,
        data: { is_active: !department.is_active },
      });
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Departments</CardTitle>
          <CardDescription>
            Optional — your internal teams (e.g. Operations, Rigging, Training), used to tag
            document owners and people.
          </CardDescription>
        </div>
        <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New department
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading departments...
          </div>
        ) : departments.length === 0 ? (
          <p className="text-sm text-muted-foreground">No departments yet.</p>
        ) : (
          departments.map((department) => (
            <div
              key={department.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3"
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{department.name}</p>
                  <Badge variant={department.is_active ? 'default' : 'secondary'}>
                    {department.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                {department.code && (
                  <p className="text-sm text-muted-foreground">{department.code}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setEditing(department)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={updateDepartment.isPending}
                  onClick={() => void toggleActive(department)}
                >
                  {department.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          ))
        )}
      </CardContent>
      <DepartmentDialog open={createOpen} onOpenChange={setCreateOpen} />
      {editing && (
        <DepartmentDialog
          department={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(undefined);
          }}
        />
      )}
    </Card>
  );
}
